import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../hooks/useNotifications';
import { useTeamChatRealtime } from '../../../hooks/useTeamChatRealtime';
import { useTeamChatSoundEnabled } from '../../../hooks/useTeamChatSoundEnabled';
import teamChatService, {
  type TeamChatConversation,
  type TeamChatMessage,
} from '../../../services/teamChatService';
import { playTeamChatPip, primeNotificationSound } from '../../../utils/notificationSound';
import { isUserViewingPage, messagePreviewText } from '../teamChatUtils';

export interface TeamChatDockThread {
  messages: TeamChatMessage[];
  loading: boolean;
  loadingOlder: boolean;
  hasMore: boolean;
  sending: boolean;
  replyTo: TeamChatMessage | null;
  editing: TeamChatMessage | null;
  typingUsers: Record<string, string>;
}

const emptyThread = (): TeamChatDockThread => ({
  messages: [],
  loading: false,
  loadingOlder: false,
  hasMore: false,
  sending: false,
  replyTo: null,
  editing: null,
  typingUsers: {},
});

const unreadEvent = () => window.dispatchEvent(new CustomEvent('team-chat:unread-refresh'));
const errorText = (error: any, fallback: string) =>
  error?.response?.data?.error || error?.message || fallback;

export function useTeamChatDockController(options: {
  maxExpanded: number;
  onUnreadChange?: (total: number) => void;
  onError?: (message: string) => void;
  browserNotificationsEnabled?: boolean;
  dockVisible?: boolean;
}) {
  const {
    maxExpanded,
    onUnreadChange,
    onError,
    browserNotificationsEnabled = true,
    dockVisible = true,
  } = options;
  const { user } = useAuth();
  const { showNotification } = useNotifications();
  const { enabledRef: soundEnabledRef, volumeRef: soundVolumeRef } = useTeamChatSoundEnabled();
  const currentUserId = user?.id || '';

  const [conversations, setConversations] = useState<TeamChatConversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [openIds, setOpenIds] = useState<string[]>([]);
  const [minimizedIds, setMinimizedIds] = useState<string[]>([]);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [threads, setThreads] = useState<Record<string, TeamChatDockThread>>({});

  const openIdsRef = useRef(openIds);
  const minimizedIdsRef = useRef(minimizedIds);
  const focusedIdRef = useRef(focusedId);
  const conversationsRef = useRef(conversations);
  const typingTimersRef = useRef<Record<string, number>>({});
  openIdsRef.current = openIds;
  minimizedIdsRef.current = minimizedIds;
  focusedIdRef.current = focusedId;
  conversationsRef.current = conversations;

  const reportError = useCallback(
    (error: any, fallback: string) => onError?.(errorText(error, fallback)),
    [onError],
  );

  const loadConversations = useCallback(async () => {
    try {
      const list = await teamChatService.getConversations();
      setConversations(list);
      onUnreadChange?.(list.reduce((sum, item) => sum + (item.unread_count || 0), 0));
      return list;
    } catch (error) {
      reportError(error, 'Não foi possível carregar as conversas.');
      return conversationsRef.current;
    } finally {
      setLoadingConversations(false);
    }
  }, [onUnreadChange, reportError]);

  useEffect(() => {
    void loadConversations();
    primeNotificationSound();
    const interval = window.setInterval(() => void loadConversations(), 45000);
    const onUnreadRefresh = () => void loadConversations();
    window.addEventListener('team-chat:unread-refresh', onUnreadRefresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('team-chat:unread-refresh', onUnreadRefresh);
    };
  }, [loadConversations]);

  const patchThread = useCallback(
    (conversationId: string, patch: Partial<TeamChatDockThread> | ((prev: TeamChatDockThread) => TeamChatDockThread)) => {
      setThreads((current) => {
        const previous = current[conversationId] || emptyThread();
        const next = typeof patch === 'function' ? patch(previous) : { ...previous, ...patch };
        return { ...current, [conversationId]: next };
      });
    },
    [],
  );

  const upsertMessage = useCallback(
    (conversationId: string, message: TeamChatMessage) => {
      patchThread(conversationId, (thread) => {
        const index = thread.messages.findIndex((item) => item.id === message.id);
        if (index < 0) return { ...thread, messages: [...thread.messages, message] };
        const messages = [...thread.messages];
        messages[index] = message;
        return { ...thread, messages };
      });
    },
    [patchThread],
  );

  const markRead = useCallback(
    async (conversationId: string) => {
      try {
        await teamChatService.markRead(conversationId);
        setConversations((current) =>
          current.map((item) => (item.id === conversationId ? { ...item, unread_count: 0 } : item)),
        );
        unreadEvent();
        const nextTotal = conversationsRef.current.reduce(
          (sum, item) => sum + (item.id === conversationId ? 0 : item.unread_count || 0),
          0,
        );
        onUnreadChange?.(nextTotal);
      } catch {
        // Leitura é best-effort.
      }
    },
    [onUnreadChange],
  );

  const loadMessages = useCallback(
    async (conversationId: string) => {
      patchThread(conversationId, { loading: true });
      try {
        const page = await teamChatService.getMessages(conversationId);
        patchThread(conversationId, {
          messages: page.messages,
          hasMore: page.has_more,
          loading: false,
        });
      } catch (error) {
        patchThread(conversationId, { loading: false });
        reportError(error, 'Não foi possível carregar as mensagens.');
      }
    },
    [patchThread, reportError],
  );

  const expandedIds = useMemo(
    () => openIds.filter((id) => !minimizedIds.includes(id)),
    [minimizedIds, openIds],
  );

  useEffect(() => {
    if (expandedIds.length <= maxExpanded) return;
    const overflow = expandedIds.slice(0, expandedIds.length - maxExpanded);
    setMinimizedIds((current) => Array.from(new Set([...current, ...overflow])));
  }, [expandedIds, maxExpanded]);

  const openConversation = useCallback(
    async (conversation: TeamChatConversation) => {
      const id = conversation.id;
      setOpenIds((current) => (current.includes(id) ? current : [...current, id]));
      setMinimizedIds((current) => {
        const restored = current.filter((item) => item !== id);
        const currentlyExpanded = openIdsRef.current.filter(
          (item) => item !== id && !restored.includes(item),
        );
        if (currentlyExpanded.length < maxExpanded) return restored;
        return [...restored, currentlyExpanded[0]];
      });
      setFocusedId(id);
      if (!threads[id]?.messages.length) await loadMessages(id);
      if (conversation.unread_count > 0) await markRead(id);
    },
    [loadMessages, markRead, maxExpanded, threads],
  );

  const minimizeConversation = useCallback((conversationId: string) => {
    setMinimizedIds((current) => (current.includes(conversationId) ? current : [...current, conversationId]));
    if (focusedIdRef.current === conversationId) {
      const remainingExpanded = openIdsRef.current.filter(
        (id) => id !== conversationId && !minimizedIdsRef.current.includes(id),
      );
      setFocusedId(remainingExpanded[remainingExpanded.length - 1] || null);
    }
  }, []);

  const minimizeAllExpanded = useCallback(() => {
    const expanded = openIdsRef.current.filter((id) => !minimizedIdsRef.current.includes(id));
    if (!expanded.length) return;
    setMinimizedIds((current) => Array.from(new Set([...current, ...expanded])));
    setFocusedId(null);
  }, []);

  const restoreConversation = useCallback(
    (conversationId: string) => {
      setMinimizedIds((current) => {
        const restored = current.filter((id) => id !== conversationId);
        const expanded = openIdsRef.current.filter(
          (id) => id !== conversationId && !restored.includes(id),
        );
        if (expanded.length < maxExpanded) return restored;
        return [...restored, expanded[0]];
      });
      setFocusedId(conversationId);
      const conversation = conversationsRef.current.find((item) => item.id === conversationId);
      if (conversation?.unread_count) void markRead(conversationId);
      void loadMessages(conversationId);
    },
    [loadMessages, markRead, maxExpanded],
  );

  const closeConversation = useCallback((conversationId: string) => {
    setOpenIds((current) => current.filter((id) => id !== conversationId));
    setMinimizedIds((current) => current.filter((id) => id !== conversationId));
    setThreads((current) => {
      const next = { ...current };
      delete next[conversationId];
      return next;
    });
    if (focusedIdRef.current === conversationId) setFocusedId(null);
  }, []);

  const handleIncomingMessage = useCallback(
    (conversationId: string, message: TeamChatMessage) => {
      if (!openIdsRef.current.includes(conversationId)) return;
      upsertMessage(conversationId, message);
      const expanded = !minimizedIdsRef.current.includes(conversationId);
      if (dockVisible && expanded && isUserViewingPage() && message.sender_id !== currentUserId) {
        void markRead(conversationId);
      }
    },
    [currentUserId, dockVisible, markRead, upsertMessage],
  );

  // Mensagens que chegaram com a janela sem foco ficam não lidas até a pessoa voltar.
  useEffect(() => {
    if (!dockVisible) return undefined;
    const onReturn = () => {
      if (!isUserViewingPage()) return;
      for (const id of openIdsRef.current) {
        if (minimizedIdsRef.current.includes(id)) continue;
        const conversation = conversationsRef.current.find((item) => item.id === id);
        if (conversation?.unread_count) void markRead(id);
      }
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [dockVisible, markRead]);

  const handleInbox = useCallback(
    async (event: {
      conversationId: string;
      message?: TeamChatMessage;
      reason: string;
      conversationType?: 'direct' | 'group' | null;
      mutedUserIds?: string[];
    }) => {
      if (event.message && openIdsRef.current.includes(event.conversationId)) {
        upsertMessage(event.conversationId, event.message);
        if (minimizedIdsRef.current.includes(event.conversationId)) {
          setConversations((current) =>
            current.map((item) =>
              item.id === event.conversationId
                ? {
                    ...item,
                    unread_count: (item.unread_count || 0) + 1,
                    last_message_preview:
                      messagePreviewText(event.message!) || item.last_message_preview,
                    last_message_at: event.message!.created_at,
                  }
                : item,
            ),
          );
        }
      }
      const list = await loadConversations();
      unreadEvent();

      const message = event.message;
      if (event.reason !== 'message' || !message || message.sender_id === currentUserId) return;
      const conversation = list.find((item) => item.id === event.conversationId);
      if (conversation?.is_muted || event.mutedUserIds?.includes(currentUserId)) return;

      const isOpenInDock = openIdsRef.current.includes(event.conversationId);
      const isExpandedVisible =
        dockVisible &&
        isOpenInDock &&
        !minimizedIdsRef.current.includes(event.conversationId) &&
        isUserViewingPage();

      if (!isExpandedVisible && !isOpenInDock && browserNotificationsEnabled) {
        const author = message.sender?.full_name || 'Colega';
        const title = conversation?.type === 'group' ? `${author} · ${conversation.title}` : author;
        void showNotification({
          title,
          body: messagePreviewText(message) || 'Nova mensagem',
          tag: `team-chat-${event.conversationId}`,
          data: { conversationId: event.conversationId },
        });
      }

      if (
        soundEnabledRef.current &&
        event.conversationType === 'direct'
      ) {
        playTeamChatPip(soundVolumeRef.current);
      }
    },
    [
      browserNotificationsEnabled,
      currentUserId,
      dockVisible,
      loadConversations,
      showNotification,
      soundEnabledRef,
      soundVolumeRef,
      upsertMessage,
    ],
  );

  const handleTyping = useCallback(
    (event: { conversationId: string; userId: string; userName?: string; isTyping: boolean }) => {
      if (event.userId === currentUserId || !openIdsRef.current.includes(event.conversationId)) return;
      const timerKey = `${event.conversationId}:${event.userId}`;
      if (typingTimersRef.current[timerKey]) window.clearTimeout(typingTimersRef.current[timerKey]);

      patchThread(event.conversationId, (thread) => {
        const typingUsers = { ...thread.typingUsers };
        if (event.isTyping) typingUsers[event.userId] = event.userName || 'Colega';
        else delete typingUsers[event.userId];
        return { ...thread, typingUsers };
      });

      if (event.isTyping) {
        typingTimersRef.current[timerKey] = window.setTimeout(() => {
          patchThread(event.conversationId, (thread) => {
            const typingUsers = { ...thread.typingUsers };
            delete typingUsers[event.userId];
            return { ...thread, typingUsers };
          });
        }, 4000);
      }
    },
    [currentUserId, patchThread],
  );

  const handleRead = useCallback(
    (event: { conversationId: string; userId: string; readAt: string }) => {
      setConversations((current) =>
        current.map((conversation) =>
          conversation.id !== event.conversationId
            ? conversation
            : {
                ...conversation,
                participants: conversation.participants.map((participant) =>
                  participant.user_id === event.userId
                    ? { ...participant, last_read_at: event.readAt }
                    : participant,
                ),
              },
        ),
      );
    },
    [],
  );

  const { connected, onlineUserIds, emitTyping } = useTeamChatRealtime({
    activeConversationIds: dockVisible ? openIds : [],
    onMessage: handleIncomingMessage,
    onMessageUpdated: upsertMessage,
    onInbox: handleInbox,
    onTyping: handleTyping,
    onRead: handleRead,
    enabled: Boolean(currentUserId),
  });

  useEffect(
    () => () => Object.values(typingTimersRef.current).forEach((timer) => window.clearTimeout(timer)),
    [],
  );

  const loadOlder = useCallback(
    async (conversationId: string) => {
      const thread = threads[conversationId];
      if (!thread || thread.loadingOlder || !thread.hasMore || !thread.messages[0]) return;
      patchThread(conversationId, { loadingOlder: true });
      try {
        const page = await teamChatService.getMessages(conversationId, {
          before: thread.messages[0].created_at,
        });
        patchThread(conversationId, (current) => ({
          ...current,
          messages: [...page.messages, ...current.messages],
          hasMore: page.has_more,
          loadingOlder: false,
        }));
      } catch (error) {
        patchThread(conversationId, { loadingOlder: false });
        reportError(error, 'Não foi possível carregar o histórico.');
      }
    },
    [patchThread, reportError, threads],
  );

  const send = useCallback(
    async (conversationId: string, body: string) => {
      const thread = threads[conversationId] || emptyThread();
      patchThread(conversationId, { sending: true });
      try {
        const message = await teamChatService.sendMessage(conversationId, {
          body,
          reply_to_id: thread.replyTo?.id || null,
        });
        upsertMessage(conversationId, message);
        patchThread(conversationId, { sending: false, replyTo: null });
        void loadConversations();
      } catch (error) {
        patchThread(conversationId, { sending: false });
        reportError(error, 'Não foi possível enviar a mensagem.');
      }
    },
    [loadConversations, patchThread, reportError, threads, upsertMessage],
  );

  const sendFile = useCallback(
    async (conversationId: string, file: File) => {
      const thread = threads[conversationId] || emptyThread();
      patchThread(conversationId, { sending: true });
      try {
        const message = await teamChatService.sendAttachment(conversationId, file, {
          replyToId: thread.replyTo?.id || null,
        });
        upsertMessage(conversationId, message);
        patchThread(conversationId, { sending: false, replyTo: null });
        void loadConversations();
      } catch (error) {
        patchThread(conversationId, { sending: false });
        reportError(error, 'Não foi possível enviar o arquivo.');
      }
    },
    [loadConversations, patchThread, reportError, threads, upsertMessage],
  );

  const submitEdit = useCallback(
    async (conversationId: string, body: string) => {
      const editing = threads[conversationId]?.editing;
      if (!editing) return;
      patchThread(conversationId, { sending: true });
      try {
        const message = await teamChatService.editMessage(editing.id, body);
        upsertMessage(conversationId, message);
        patchThread(conversationId, { sending: false, editing: null });
      } catch (error) {
        patchThread(conversationId, { sending: false });
        reportError(error, 'Não foi possível editar a mensagem.');
      }
    },
    [patchThread, reportError, threads, upsertMessage],
  );

  const deleteMessage = useCallback(
    async (conversationId: string, message: TeamChatMessage) => {
      if (!window.confirm('Apagar esta mensagem?')) return;
      try {
        upsertMessage(conversationId, await teamChatService.deleteMessage(message.id));
        void loadConversations();
      } catch (error) {
        reportError(error, 'Não foi possível apagar a mensagem.');
      }
    },
    [loadConversations, reportError, upsertMessage],
  );

  const toggleReaction = useCallback(
    async (conversationId: string, message: TeamChatMessage, emoji: string) => {
      try {
        upsertMessage(conversationId, await teamChatService.toggleReaction(message.id, emoji));
      } catch (error) {
        reportError(error, 'Não foi possível reagir à mensagem.');
      }
    },
    [reportError, upsertMessage],
  );

  const startDirect = useCallback(
    async (userId: string) => {
      const conversation = await teamChatService.openDirect(userId);
      await loadConversations();
      await openConversation(conversation);
    },
    [loadConversations, openConversation],
  );

  const createGroup = useCallback(
    async (payload: { title: string; description?: string; participant_ids: string[] }) => {
      const conversation = await teamChatService.createGroup(payload);
      await loadConversations();
      await openConversation(conversation);
    },
    [loadConversations, openConversation],
  );

  return {
    conversations,
    loadingConversations,
    connected,
    onlineUserIds,
    openIds,
    minimizedIds,
    expandedIds,
    focusedId,
    threads,
    currentUserId,
    loadConversations,
    openConversation,
    minimizeConversation,
    minimizeAllExpanded,
    restoreConversation,
    closeConversation,
    setFocusedId,
    loadOlder,
    send,
    sendFile,
    submitEdit,
    deleteMessage,
    toggleReaction,
    emitTyping,
    setReplyTo: (conversationId: string, message: TeamChatMessage | null) =>
      patchThread(conversationId, { replyTo: message, editing: null }),
    setEditing: (conversationId: string, message: TeamChatMessage | null) =>
      patchThread(conversationId, { editing: message, replyTo: null }),
    startDirect,
    createGroup,
    showError: (message: string) => onError?.(message),
  };
}

export default useTeamChatDockController;
